// Hooks de Chatwoot API vía Edge Function proxy (chatwoot-api)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface CwConversation {
  id: number
  status: string
  meta?: { sender?: { id?: number; name?: string; phone_number?: string | null; thumbnail?: string | null } }
  messages?: { content: string | null; created_at: number; message_type: number }[]
  can_reply?: boolean
  unread_count?: number
}

export interface CwMessage {
  id: number
  content: string | null
  message_type: number
  created_at: number
  private?: boolean
  sender?: { name?: string; type?: string }
  attachments?: { file_url: string; file_type?: string }[]
}

async function callCw(action: string, body: Record<string, unknown> = {}) {
  const { data, error } = await supabase.functions.invoke('chatwoot-api', {
    body: { action, ...body },
  })
  if (error) throw error
  return data
}

export function useCwConversations(status: 'open' | 'resolved' | 'pending' = 'open') {
  return useQuery({
    queryKey: ['cw', 'conversations', status],
    queryFn: async (): Promise<CwConversation[]> => {
      const r = await callCw('list_conversations', { status })
      return (r?.data?.payload ?? []) as CwConversation[]
    },
    refetchInterval: 10_000,
    staleTime: 5_000,
  })
}

export function useCwMessages(conversationId: number | null) {
  return useQuery({
    queryKey: ['cw', 'messages', conversationId],
    enabled: !!conversationId,
    queryFn: async (): Promise<CwMessage[]> => {
      const r = await callCw('list_messages', { conversation_id: conversationId })
      return (r?.payload ?? []) as CwMessage[]
    },
    refetchInterval: 5_000,
  })
}

export function useCwSendMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { conversation_id: number; content: string; private?: boolean }) => {
      return await callCw('send_message', args)
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['cw', 'messages', vars.conversation_id] })
      qc.invalidateQueries({ queryKey: ['cw', 'conversations'] })
    },
  })
}

export function useCwToggleStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { conversation_id: number; status: 'open' | 'resolved' | 'pending' }) => {
      return await callCw('toggle_status', args)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cw', 'conversations'] })
    },
  })
}
